import { EN_BLOG_SOURCE } from "./en/journal-en-blog.data";
/**
 * Eén bron van waarheid voor de koppeling tussen de Nederlandse (nl-NL) en de
 * Engelse (en) versie van Taxiritje.nl.
 *
 * De Nederlandse URL-structuur blijft ongewijzigd; de Engelse versie leeft
 * volledig onder /en/ met eigen, Engelse slugs. Deze module levert:
 *  - de statische padmapping NL <-> EN
 *  - regels voor dynamische pagina's (plaatsen, gemeenten, prijzen, journal)
 *  - hreflang-links (nl-NL, en, x-default) voor beide varianten
 *
 * Alleen publieke, indexeerbare pagina's staan hierin. Portalen, dashboards,
 * betaal- en chauffeurspagina's krijgen bewust geen Engelse variant en dus ook
 * geen hreflang.
 */

export const SITE = "https://taxiritje.nl";

/** NL-pad -> EN-pad voor alle statische publieke pagina's. */
export const STATIC_NL_TO_EN: Record<string, string> = {
  "/": "/en",
  "/boeken": "/en/book",
  "/prijzen": "/en/prices",
  "/wat-kost-een-taxi": "/en/what-does-a-taxi-cost",
  "/luchthaventaxi": "/en/airport-taxi",
  "/taxi-reserveren": "/en/book-a-taxi-in-advance",
  "/goedkope-taxi": "/en/cheap-taxi",
  "/stationstaxi": "/en/train-station-taxi",
  "/taxi-naar-buitenlandse-luchthavens": "/en/taxi-to-foreign-airports",
  "/taxi-naar-brussel-en-charleroi": "/en/taxi-to-brussels-airport-and-charleroi",
  "/taxibus-8-personen": "/en/minibus-taxi-8-passengers",
  "/taxibus-prijzen": "/en/minibus-taxi-prices",
  "/taxi-prijs-berekenen": "/en/taxi-fare-calculator",
  "/taxi-tarieven": "/en/taxi-rates",
  "/taxi-lange-afstand": "/en/long-distance-taxi",
  "/taxi-in-de-buurt": "/en/taxi-near-me",
  "/diensten": "/en/services",
  "/zakelijk": "/en/business",
  "/hoe-het-werkt": "/en/how-it-works",
  "/faq": "/en/faq",
  "/journal": "/en/journal",
  "/taxi": "/en/taxi",
  "/contact": "/en/contact",
  "/over-ons": "/en/about-us",
  "/veiligheid-en-kwaliteit": "/en/safety-and-quality",
  "/annuleringsbeleid": "/en/cancellation-policy",
  "/klachten": "/en/complaints",
  "/voorwaarden": "/en/terms",
  "/privacy": "/en/privacy",
  "/cookies": "/en/cookies",
  "/disclaimer": "/en/disclaimer",
  "/partners": "/en/partners",
  "/alle-paginas": "/en/all-pages",
};

export const STATIC_EN_TO_NL: Record<string, string> = Object.fromEntries(
  Object.entries(STATIC_NL_TO_EN).map(([nl, en]) => [en, nl]),
);

/** Journal: Nederlandse slug -> Engelse slug (eigen, native Engelse URL). */
export const JOURNAL_NL_TO_EN: Record<string, string> = {
  ...Object.fromEntries(EN_BLOG_SOURCE.map((b) => [b.nlSlug, b.slug])),
  "taxi-of-trein": "taxi-or-train",
  "taxi-of-openbaar-vervoer": "taxi-or-public-transport",
  "taxi-of-eigen-auto": "taxi-or-your-own-car",
  "taxi-naar-het-vliegveld-of-zelf-rijden": "taxi-to-the-airport-or-drive-yourself",
  "parkeren-bij-het-vliegveld-of-taxi": "airport-parking-or-taxi",
  "wanneer-is-een-taxi-de-slimste-keuze": "when-is-a-taxi-the-smartest-choice",
  "luchthaventaxi-hoe-laat-vertrekken": "what-time-to-leave-for-the-airport",
  "taxi-naar-het-vliegveld-bij-een-vroege-vlucht": "taxi-to-the-airport-for-an-early-flight",
  "taxi-naar-het-vliegveld-met-kinderen": "taxi-to-the-airport-with-children",
  "met-veel-bagage-naar-het-vliegveld": "to-the-airport-with-a-lot-of-luggage",
  "vertraging-van-trein-of-vlucht": "delayed-train-or-flight",
  "taxi-naar-een-station": "taxi-to-a-train-station",
  "taxi-naar-het-ziekenhuis": "taxi-to-a-hospital",
  "taxi-naar-een-hotel": "taxi-to-a-hotel",
  "taxi-naar-een-stadion-of-evenement": "taxi-to-a-stadium-or-event",
  "taxi-naar-een-stadscentrum-of-winkelgebied": "taxi-to-a-city-centre-or-shopping-area",
  "met-kinderen-in-de-taxi": "travelling-with-children-in-a-taxi",
  "met-huisdieren-in-de-taxi": "travelling-with-pets-in-a-taxi",
  "reizen-als-u-slecht-ter-been-bent": "travelling-with-reduced-mobility",
  "reizen-met-een-groep": "travelling-as-a-group",
  "taxibus-met-meerdere-personen": "minibus-taxi-for-several-passengers",
  "hoe-lang-van-tevoren-taxi-reserveren": "how-far-in-advance-to-book-a-taxi",
  "taxi-in-de-nacht": "taxi-at-night",
  "taxi-tijdens-de-spits": "taxi-during-rush-hour",
  "rit-wijzigen-of-annuleren": "change-or-cancel-a-booking",
  "vaste-prijs-versus-taxameter": "fixed-price-versus-taximeter",
  "zakelijk-taxivervoer-facturatie": "business-taxi-travel-and-invoicing",
  "voordelen-van-vooraf-reserveren": "benefits-of-booking-in-advance",
};

export const JOURNAL_EN_TO_NL: Record<string, string> = Object.fromEntries(
  Object.entries(JOURNAL_NL_TO_EN).map(([nl, en]) => [en, nl]),
);

function stripSlash(path: string) {
  return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
}

/**
 * NL-only pagina's: deze hebben bewust geen Engelse variant. Ze mogen niet
 * via een fallback-regel een /en/-tegenhanger krijgen, want die route
 * bestaat niet (dode URL in sitemap-en.xml en hreflang).
 */
const NL_ONLY_PATHS = new Set<string>([
  "/taxi-bus-reddit",
  "/taxi-rotterdam-reddit",
  "/schiphol-taxi-reddit",
]);

/** Engelse tegenhanger van een Nederlands pad, of null als die niet bestaat. */
export function enPathForNl(nlPath: string): string | null {
  const path = stripSlash(nlPath);
  if (NL_ONLY_PATHS.has(path)) return null;
  const stat = STATIC_NL_TO_EN[path];
  if (stat) return stat;

  let m = /^\/taxi\/([a-z0-9-]+)\/schiphol$/.exec(path);
  if (m) return `/en/taxi/${m[1]}/schiphol`;

  m = /^\/taxi\/([a-z0-9-]+)\/brussel$/.exec(path);
  if (m) return `/en/taxi/${m[1]}/brussels`;

  m = /^\/taxi\/gemeente\/([a-z0-9-]+)$/.exec(path);
  if (m) return `/en/taxi/gemeente/${m[1]}`;

  m = /^\/taxi\/([a-z0-9-]+)$/.exec(path);
  if (m) return `/en/taxi/${m[1]}`;

  m = /^\/prijzen\/([a-z0-9-]+)$/.exec(path);
  if (m) return `/en/prices/${m[1]}`;

  m = /^\/journal\/([a-z0-9-]+)$/.exec(path);
  if (m) {
    const en = JOURNAL_NL_TO_EN[m[1]!];
    return en ? `/en/journal/${en}` : null;
  }

  m = /^\/(taxi-[a-z0-9-]+)$/.exec(path);
  if (m) return `/en/${m[1]}`;

  return null;
}

/** Nederlandse tegenhanger van een Engels pad, of null als die niet bestaat. */
export function nlPathForEn(enPath: string): string | null {
  const path = stripSlash(enPath);
  const stat = STATIC_EN_TO_NL[path];
  if (stat) return stat;

  let m = /^\/en\/taxi\/([a-z0-9-]+)\/schiphol$/.exec(path);
  if (m) return `/taxi/${m[1]}/schiphol`;

  m = /^\/en\/taxi\/([a-z0-9-]+)\/brussels$/.exec(path);
  if (m) return `/taxi/${m[1]}/brussel`;

  m = /^\/en\/taxi\/gemeente\/([a-z0-9-]+)$/.exec(path);
  if (m) return `/taxi/gemeente/${m[1]}`;

  m = /^\/en\/taxi\/([a-z0-9-]+)$/.exec(path);
  if (m) return `/taxi/${m[1]}`;

  m = /^\/en\/prices\/([a-z0-9-]+)$/.exec(path);
  if (m) return `/prijzen/${m[1]}`;

  m = /^\/en\/journal\/([a-z0-9-]+)$/.exec(path);
  if (m) {
    const nl = JOURNAL_EN_TO_NL[m[1]!];
    return nl ? `/journal/${nl}` : null;
  }

  m = /^\/en\/(taxi-[a-z0-9-]+)$/.exec(path);
  if (m) return `/${m[1]}`;

  return null;
}

export type HeadLink = { rel: string; href: string; hrefLang?: string };

/**
 * Bidirectionele hreflang-set. De Nederlandse pagina is altijd x-default:
 * Taxiritje.nl is een Nederlandse dienst en NL is de standaardtaal.
 * Bestaat er geen tegenhanger, dan worden er geen hreflang-tags gezet.
 */
export function hreflangLinks(currentPath: string): HeadLink[] {
  const isEn = stripSlash(currentPath) === "/en" || currentPath.startsWith("/en/");
  const nl = isEn ? nlPathForEn(currentPath) : stripSlash(currentPath);
  const en = isEn ? stripSlash(currentPath) : enPathForNl(currentPath);
  if (!nl || !en) return [];
  const nlHref = `${SITE}${nl === "/" ? "/" : nl}`;
  const enHref = `${SITE}${en}`;
  return [
    { rel: "alternate", hrefLang: "nl-NL", href: nlHref },
    { rel: "alternate", hrefLang: "en", href: enHref },
    { rel: "alternate", hrefLang: "en-NL", href: enHref },
    { rel: "alternate", hrefLang: "x-default", href: nlHref },
  ];
}
